/**
 * DOMHelper - Utility functions for simplified DOM creation
 * Reduces repetitive DOM manipulation code without using template strings
 */
export default class DOMHelper {
	/**
	 * Create an element with optional className, textContent, and attributes
	 * @param {string} tag - HTML tag name
	 * @param {string} [className] - CSS class name(s)
	 * @param {string} [textContent] - Text content
	 * @param {Object} [attributes] - Element attributes as key-value pairs
	 * @returns {HTMLElement} Created element
	 */
	static createElement(
		tag,
		className = null,
		textContent = null,
		attributes = {},
	) {
		const element = document.createElement(tag);

		if (className) {
			element.className = className;
		}

		if (textContent) {
			element.textContent = textContent;
		}

		// Apply attributes
		Object.entries(attributes).forEach(([key, value]) => {
			if (key === "dataset") {
				// Handle data attributes
				Object.entries(value).forEach(([dataKey, dataValue]) => {
					element.dataset[dataKey] = dataValue;
				});
			} else {
				element[key] = value;
			}
		});

		return element;
	}

	/**
	 * Create a button with icon
	 * @param {string} className - Button CSS class
	 * @param {string} title - Button title/tooltip
	 * @param {string} icon - Icon text/emoji
	 * @param {Object} [attributes] - Additional attributes
	 * @returns {HTMLElement} Button element with icon
	 */
	static createButton(className, title, icon, attributes = {}) {
		const button = this.createElement(
			"button",
			`tjs-btn ${className}`,
			null,
			{
				title,
				...attributes,
			},
		);

		const iconSpan = this.createElement("span", "tjs-icon", icon);
		button.appendChild(iconSpan);

		return button;
	}

	/**
	 * Create an input element with attributes
	 * @param {string} type - Input type
	 * @param {string} className - CSS class name
	 * @param {Object} [attributes] - Input attributes
	 * @returns {HTMLElement} Input element
	 */
	static createInput(type, className, attributes = {}) {
		return this.createElement("input", className, null, {
			type,
			...attributes,
		});
	}

	/**
	 * Create a labeled input group (label + input + optional value display)
	 * @param {string} labelText - Label text
	 * @param {string} inputType - Input type
	 * @param {string} inputClass - Input CSS class
	 * @param {Object} inputAttributes - Input attributes
	 * @param {string} [valueClass] - Value display CSS class
	 * @param {string} [initialValue] - Initial value text
	 * @returns {HTMLElement} Setting group element
	 */
	static createLabeledInput(
		labelText,
		inputType,
		inputClass,
		inputAttributes = {},
		valueClass = null,
		initialValue = null,
	) {
		const group = this.createElement("div", "tjs-setting-group");

		const label = this.createElement("label", "tjs-label", labelText);
		group.appendChild(label);

		const input = this.createInput(
			inputType,
			`tjs-slider ${inputClass}`,
			inputAttributes,
		);
		group.appendChild(input);

		if (valueClass && initialValue) {
			const valueSpan = this.createElement(
				"span",
				`tjs-value ${valueClass}`,
				initialValue,
			);
			group.appendChild(valueSpan);
		}

		return group;
	}

	/**
	 * Create a checkbox with label
	 * @param {string} labelText - Label text
	 * @param {string} checkboxClass - Checkbox CSS class
	 * @param {boolean} [checked] - Initial checked state
	 * @returns {HTMLElement} Checkbox group element
	 */
	static createCheckbox(labelText, checkboxClass, checked = false) {
		const group = this.createElement("div", "tjs-setting-group");

		const label = this.createElement("label", "tjs-checkbox-label");

		const checkbox = this.createInput("checkbox", checkboxClass, {
			checked,
		});
		label.appendChild(checkbox);

		const checkmark = this.createElement("span", "tjs-checkmark");
		label.appendChild(checkmark);

		label.appendChild(document.createTextNode(labelText));
		group.appendChild(label);

		return group;
	}

	/**
	 * Create a checkbox with color picker
	 * @param {string} labelText - Label text
	 * @param {string} checkboxClass - Checkbox CSS class
	 * @param {string} colorClass - Color picker CSS class
	 * @param {string} defaultColor - Default color value
	 * @returns {HTMLElement} Checkbox + color picker group
	 */
	static createCheckboxColor(
		labelText,
		checkboxClass,
		colorClass,
		defaultColor,
	) {
		const group = this.createElement("div", "tjs-setting-group");

		const label = this.createElement("label", "tjs-checkbox-label");

		const checkbox = this.createInput("checkbox", checkboxClass);
		label.appendChild(checkbox);

		const checkmark = this.createElement("span", "tjs-checkmark");
		label.appendChild(checkmark);

		label.appendChild(document.createTextNode(labelText));
		group.appendChild(label);

		const colorPicker = this.createInput(
			"color",
			`tjs-color-picker ${colorClass}`,
			{
				value: defaultColor,
			},
		);
		group.appendChild(colorPicker);

		return group;
	}

	/**
	 * Append multiple children to a parent element
	 * @param {HTMLElement} parent - Parent element
	 * @param {HTMLElement[]} children - Array of child elements
	 */
	static appendChildren(parent, children) {
		children.forEach((child) => {
			if (child) {
				parent.appendChild(child);
			}
		});
	}

	/**
	 * Create a container div with className and children
	 * @param {string} className - Container CSS class
	 * @param {HTMLElement[]} children - Child elements
	 * @returns {HTMLElement} Container element
	 */
	static createContainer(className, children = []) {
		const container = this.createElement("div", className);
		this.appendChildren(container, children);
		return container;
	}
}
